import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { Dictionary } from '@empathyco/x-utils'
import type { Route, RouteRecord } from 'vue-router'
import type { Store } from 'vuex'
import type {
  AppRoute,
  ContextState,
  Filter,
  FilterSelectedValues,
  GuardResolveFunction,
} from '../models'
import type { ContextProvider } from '../providers'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { deepMerge } from '@empathyco/x-deep-merge'
import { deepEqual } from '@empathyco/x-utils'
import { getDefaultEndpointId } from '../composables/use-endpoints'
import { CONTAINER } from '../config'
import { TYPES } from '../config/inversify.types'
import { FilterTypeEnum } from '../enums'
import { GuardClass } from '../models'
import { ROUTER_CONSTS } from '../router/router.consts'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { getStateToolKey } from '../store/context/utils/get-state-tool-key'
import { FiltersUtils } from '../utils'

export class SetContextWithUrlGuard extends GuardClass {
  static async execute(
    this: void,
    to: AppRoute,
    from: Route,
    next: GuardResolveFunction,
  ): Promise<void> {
    const instanceConfig = await SetContextWithUrlGuard.getInstanceConfig(to)

    if (instanceConfig && to.name !== from.name) {
      const store = CONTAINER.get<Store<any>>(TYPES.Store)

      store.dispatch(`${CONTEXT_CONSTS.moduleName}/${CONTEXT_CONSTS.actions.setContext}`, {
        currentFeature: to?.meta?.featureId,
      })

      const context = SetContextWithUrlGuard.getContext(true)

      const toolStateKey = getStateToolKey({ ...context, currentFeature: to?.meta?.featureId })
      const persistedSelectedFiltersValues =
        store.state[CONTEXT_CONSTS.moduleName]?.[toolStateKey]?.selectedFiltersValues

      const filters = await FiltersUtils.getFiltersFromContextAndRoute(
        context,
        to,
        instanceConfig,
        {
          persistedSelectedFiltersValues,
          toolStateKey,
        },
      )
      const filtersContext: Dictionary<Filter> = {}
      const filtersSelectedValues: Dictionary<FilterSelectedValues> = {}

      Object.keys(filters).forEach(key => {
        const filter = filters[key]

        filtersContext[key] = deepMerge({}, filter)

        if (
          filter.config.type === FilterTypeEnum.REGULAR &&
          Array.isArray(filter.selectedValues) &&
          filter.selectedValues.length
        ) {
          filtersSelectedValues[key] = filter.selectedValues
          filtersContext[key].selectedValues = []
        }
      })

      let domain: DomainEnum | undefined
      to.matched.some((route: RouteRecord) => {
        if (route.meta) {
          domain = route.meta.domain as DomainEnum
        }
        return !!domain
      })
      domain = domain || DomainEnum.GLOBAL

      const currentContext = SetContextWithUrlGuard.getContext(true)

      const newContext: ContextState = {
        instanceId: instanceConfig.id,
        domain,
        filters: filtersContext,
        endpointId:
          currentContext.endpointId === 'search'
            ? getDefaultEndpointId()
            : currentContext.endpointId,
      }

      store.dispatch(
        `${CONTEXT_CONSTS.moduleName}/${CONTEXT_CONSTS.actions.setContext}`,
        newContext,
      )

      const featureContext = SetContextWithUrlGuard.getContext(true)

      const query = currentContext.query || ''
      store.dispatch(`${CONTEXT_CONSTS.moduleName}/${CONTEXT_CONSTS.actions.setQuery}`, query)

      const strictMatching = SetContextWithUrlGuard.getStrictMatching(to, featureContext)
      store.dispatch(
        `${CONTEXT_CONSTS.moduleName}/${CONTEXT_CONSTS.actions.setStrictMatching}`,
        strictMatching,
      )

      store.dispatch(
        `${CONTEXT_CONSTS.moduleName}/${CONTEXT_CONSTS.actions.setFiltersSelectedValues}`,
        filtersSelectedValues,
      )

      const contextRoute = SetContextWithUrlGuard.updateRouteWithContext(
        to,
        SetContextWithUrlGuard.getContext(true),
      )

      if (!deepEqual(contextRoute, to)) {
        next({
          ...contextRoute,
          replace: true,
        })

        return
      }
    }

    next()
  }

  static updateRouteWithContext(route: Route, context: ContextState): Route {
    let updatedRoute: AppRoute = {
      ...route,
      params: {
        ...route.params,
        [ROUTER_CONSTS.params.instanceId]: context.instanceId,
      },
      query: { ...route.query },
    } as AppRoute

    if (typeof context.strictMatching === 'boolean') {
      updatedRoute.query[ROUTER_CONSTS.query.strictMatching] = context.strictMatching.toString()
    }

    updatedRoute = FiltersUtils.updateRouteFiltersWithContext(updatedRoute, context)

    return updatedRoute
  }

  // TODO: Move this somewhere else (eg: a getter) and use it here an in AuthorizeRouteGuard
  private static async getInstanceConfig(route: Route): Promise<Instance | undefined> {
    const store = CONTAINER.get<Store<any>>(TYPES.Store)
    const instances: Instance[] =
      await store.getters[`${CONFIG_CONSTS.moduleName}/${CONFIG_CONSTS.getters.instances}`]

    let instanceConfig: Instance | undefined
    const context = this.getContext()
    const instanceId = context.instanceId || route.params[ROUTER_CONSTS.params.instanceId]
    if (instanceId) {
      instanceConfig = instances.find((instance: Instance) => instance.id === instanceId)
    }
    if (!instanceConfig && instances.length === 1) {
      instanceConfig = instances[0]
    }
    return instanceConfig
  }

  private static getStrictMatching(route: Route, context: ContextState): boolean | undefined {
    const paramValue = route.query[ROUTER_CONSTS.query.strictMatching]
    const contextValue = context.strictMatching

    return contextValue ?? (typeof paramValue === 'undefined' ? undefined : paramValue === 'true')
  }

  private static getContext(useCurretFeatureContext = false): ContextState {
    return CONTAINER.get<ContextProvider>(TYPES.ContextProvider)(useCurretFeatureContext)
  }
}
